import { type TranscriptState } from '@apps/web/features/phoebe_chat_v3/model/state';
import {
    runStateWaitingFor,
    type ThinkingEvidence,
    type WaitingForInput,
} from '@apps/web/features/phoebe_chat_v3/model/transcript_store';

/**
 * The single authoritative answer to "where is Phoebe's current response?".
 *
 * Every liveness-dependent surface — the Thinking row, the live activity
 * group, the stop control, follow-up holds — must derive from this phase
 * instead of blending raw evidence, so a stuck or phantom state can only
 * come from one precedence order in one function.
 */
export type ResponsePhase =
    | Readonly<{ readonly kind: 'idle' }>
    | Readonly<{
          readonly kind: 'needs_attention';
          readonly reason: 'send_failed';
      }>
    | Readonly<{ readonly kind: 'sending' }>
    | Readonly<{
          readonly kind: 'working';
          readonly source: 'generating' | 'queued' | 'background';
          /**
           * The run keeps its lease while a tool holds for a permission or
           * a reply, so a working run can simultaneously be blocked on the
           * user. Surfaces read it through responsePhaseWaitingFor rather
           * than re-deriving it from raw run-state flags.
           */
          readonly waitingFor: WaitingForInput | null;
      }>
    | Readonly<{
          readonly kind: 'waiting';
          readonly waitingFor: WaitingForInput;
      }>
    | Readonly<{ readonly kind: 'stopping' }>;

export type ResponsePhaseProjection = Readonly<{
    readonly phase: ResponsePhase;
    /**
     * The latest response reached an authoritative end: a terminal run event
     * closed the current segment, or the run state reports terminal with no
     * newer submitted turn. An older Stop can resolve through its own
     * boundary while this is false for the next response.
     */
    readonly runEnded: boolean;
    /** The response targeted by the local Stop ended, even if a newer one began. */
    readonly stoppedResponseEnded: boolean;
}>;

const TERMINAL_RUN_EVENT_TYPES = new Set([
    'run_cancelled',
    'run_completed',
    'run_failed',
    'run_interrupted',
]);
const STOP_REQUEST_EVENT_TYPES = new Set([
    'interrupt_requested',
    'stop_requested',
]);

/** Shared terminal-event classification for response segment projections. */
export function isTerminalResponseEvent(eventType: string): boolean {
    return TERMINAL_RUN_EVENT_TYPES.has(eventType);
}

/** A Stop's response can end and a new response start within one poll. */
export function responseEndedAfter(
    state: TranscriptState,
    afterEventOffset: number,
): boolean {
    for (const event of state.committedById.values()) {
        if (
            event.event_offset > afterEventOffset &&
            isTerminalResponseEvent(event.type)
        ) {
            return true;
        }
    }
    return false;
}

export function responsePhaseIsLive(phase: ResponsePhase): boolean {
    return (
        phase.kind === 'sending' ||
        phase.kind === 'working' ||
        phase.kind === 'stopping'
    );
}

/**
 * What the current response is blocked on from the user, if anything. Both
 * a waiting phase and a working run whose tool holds for the user report
 * here, so waiting-dependent surfaces share one precedence order.
 */
export function responsePhaseWaitingFor(
    phase: ResponsePhase,
): WaitingForInput | null {
    if (phase.kind === 'waiting' || phase.kind === 'working') {
        return phase.waitingFor;
    }
    return null;
}

type LatestSegment = Readonly<{
    readonly terminal: boolean;
    readonly openSubmittedTurn: boolean;
    /**
     * A committed stop request with nothing newer after it. The server took
     * the stop and the run produced nothing since, so the response is over
     * for the UI even before the terminal event commits — which can also
     * arrive before the stop's own echo.
     */
    readonly stopIsLatest: boolean;
}>;

/**
 * Walk the committed tail back to the latest run boundary. A user message
 * after a terminal event opens a new turn, so that terminal event no longer
 * ends the conversation's current response.
 */
function projectLatestSegment(state: TranscriptState): LatestSegment {
    let terminal = false;
    let openSubmittedTurn = false;
    let stopIsLatest = false;
    let sawEventSinceTail = false;
    for (let index = state.committedOrder.length - 1; index >= 0; index -= 1) {
        const eventId = state.committedOrder[index];
        const event =
            eventId === undefined
                ? undefined
                : state.committedById.get(eventId);
        if (event === undefined) {
            continue;
        }
        if (isTerminalResponseEvent(event.type)) {
            terminal = !openSubmittedTurn;
            break;
        }
        if (STOP_REQUEST_EVENT_TYPES.has(event.type)) {
            if (!sawEventSinceTail) {
                stopIsLatest = true;
            }
            continue;
        }
        if (event.type === 'user_message') {
            openSubmittedTurn = true;
        }
        sawEventSinceTail = true;
        if (event.type === 'run_started') {
            break;
        }
    }
    return { terminal, openSubmittedTurn, stopIsLatest };
}

/**
 * Whether the Thinking row belongs under an answer that is the newest
 * visible content. Normally it would only lag the run's terminal event, but a
 * check-in or watch can wake the run after the answer with no new message.
 * Only live generation of that new work qualifies, so queued or long
 * background work never leaves the petal spinning under the answer.
 */
export function responseThinkingFollowsAnswer(
    phase: ResponsePhase,
    state: TranscriptState,
): boolean {
    return (
        phase.kind === 'working' &&
        phase.source === 'generating' &&
        projectAnswerTail(state).runStartedAfterAnswer
    );
}

/** Where Phoebe's latest committed answer sits in the transcript tail. */
export type AnswerTail = Readonly<{
    readonly latestAnswerEventId: string | null;
    /**
     * A run started after that answer, such as a check-in waking the run.
     * The tail of the answer's own run never has one.
     */
    readonly runStartedAfterAnswer: boolean;
    readonly userMessageAfterAnswer: boolean;
}>;

/** The one walk from the newest committed event back to the latest answer. */
export function projectAnswerTail(state: TranscriptState): AnswerTail {
    let runStartedAfterAnswer = false;
    let userMessageAfterAnswer = false;
    for (let index = state.committedOrder.length - 1; index >= 0; index -= 1) {
        const eventId = state.committedOrder[index];
        const event =
            eventId === undefined
                ? undefined
                : state.committedById.get(eventId);
        if (event?.type === 'assistant_text') {
            return {
                latestAnswerEventId: event.id,
                runStartedAfterAnswer,
                userMessageAfterAnswer,
            };
        }
        if (event?.type === 'run_started') {
            runStartedAfterAnswer = true;
        }
        if (event?.type === 'user_message') {
            userMessageAfterAnswer = true;
        }
    }
    return {
        latestAnswerEventId: null,
        runStartedAfterAnswer: false,
        userMessageAfterAnswer: false,
    };
}

function hasUnechoedSend(
    state: TranscriptState,
    stoppedUserTurnIds: ReadonlySet<string> | undefined,
    echoCanArrive: boolean,
): boolean {
    for (const message of state.optimisticByOperationId.values()) {
        if (
            (message.status === 'posting' ||
                (echoCanArrive && message.status === 'awaiting_echo')) &&
            message.matchedPendingId === null &&
            message.matchedCommittedId === null &&
            !stoppedUserTurnIds?.has(message.operationId)
        ) {
            return true;
        }
    }
    return false;
}

/**
 * A send whose POST failed and whose durable echo never arrived. A failed
 * send that did echo was delivered — the committed event is its record and
 * nothing about it needs attention.
 */
function hasFailedUnechoedSend(state: TranscriptState): boolean {
    for (const message of state.optimisticByOperationId.values()) {
        if (
            message.status === 'failed' &&
            message.matchedPendingId === null &&
            message.matchedCommittedId === null
        ) {
            return true;
        }
    }
    return false;
}

export type StopRequestBoundary = Readonly<{
    readonly afterEventOffset: number;
    readonly queuedUserTurnIds: ReadonlySet<string>;
    readonly knownPendingStopIds: ReadonlySet<string>;
}>;

export function captureStopRequestBoundary(
    state: TranscriptState,
): StopRequestBoundary {
    const queuedUserTurnIds = new Set<string>();
    const knownPendingStopIds = new Set<string>();
    for (const pending of state.pendingById.values()) {
        if (pending.type === 'user_message' && pending.status === 'pending') {
            queuedUserTurnIds.add(pending.id);
        }
        if (STOP_REQUEST_EVENT_TYPES.has(pending.type)) {
            knownPendingStopIds.add(pending.id);
        }
    }
    for (const message of state.optimisticByOperationId.values()) {
        if (
            message.status === 'posting' ||
            message.status === 'awaiting_echo'
        ) {
            queuedUserTurnIds.add(message.operationId);
        }
    }
    return {
        afterEventOffset: state.cursor.after_offset,
        queuedUserTurnIds,
        knownPendingStopIds,
    };
}

export type StopRequestProjection = Readonly<{
    readonly recorded: boolean;
    readonly echoed: boolean;
    readonly acknowledged: boolean;
    readonly stoppedResponseEnded: boolean;
    readonly stoppedUserTurnIds: ReadonlySet<string> | undefined;
}>;

export function projectStopRequests(
    state: TranscriptState,
    boundaries: ReadonlyMap<string, StopRequestBoundary> = new Map(),
): Readonly<{
    readonly current: StopRequestProjection;
    readonly byRequestId: ReadonlyMap<string, StopRequestProjection>;
}> {
    let pendingStopRecorded = false;
    const drainedStops: Array<
        Readonly<{ id: string; eventOffset: number | null }>
    > = [];
    for (const pending of state.pendingById.values()) {
        if (!STOP_REQUEST_EVENT_TYPES.has(pending.type)) {
            continue;
        }
        if (pending.status === 'pending') {
            pendingStopRecorded = true;
        }
        if (
            pending.status === 'drained' &&
            pending.drained_into_event_id !== null
        ) {
            drainedStops.push({
                id: pending.id,
                eventOffset:
                    state.committedById.get(pending.drained_into_event_id)
                        ?.event_offset ?? null,
            });
        }
    }
    let latestStopOffset = Number.NEGATIVE_INFINITY;
    let latestTerminalOffset = Number.NEGATIVE_INFINITY;
    for (const event of state.committedById.values()) {
        if (STOP_REQUEST_EVENT_TYPES.has(event.type)) {
            latestStopOffset = Math.max(latestStopOffset, event.event_offset);
        }
        if (isTerminalResponseEvent(event.type)) {
            latestTerminalOffset = Math.max(
                latestTerminalOffset,
                event.event_offset,
            );
        }
    }
    const recorded =
        pendingStopRecorded ||
        (state.runState?.interrupt_requested === true &&
            !projectLatestSegment(state).terminal);
    const current: StopRequestProjection = {
        recorded,
        echoed: false,
        acknowledged: false,
        stoppedResponseEnded: false,
        stoppedUserTurnIds: undefined,
    };
    const byRequestId = new Map<string, StopRequestProjection>();
    for (const [requestId, boundary] of boundaries) {
        const echoed =
            pendingStopRecorded ||
            latestStopOffset > boundary.afterEventOffset ||
            drainedStops.some(
                (stop) =>
                    !boundary.knownPendingStopIds.has(stop.id) &&
                    (stop.eventOffset === null ||
                        stop.eventOffset > boundary.afterEventOffset),
            );
        const stoppedResponseEnded =
            latestTerminalOffset > boundary.afterEventOffset;
        byRequestId.set(requestId, {
            recorded,
            echoed,
            acknowledged: recorded || echoed,
            stoppedResponseEnded,
            stoppedUserTurnIds: stoppedResponseEnded
                ? undefined
                : boundary.queuedUserTurnIds,
        });
    }
    return { current, byRequestId };
}

/**
 * A user message the server holds but has not drained opens a new response:
 * an older stop or terminal event must not read as this response's end, or
 * the follow-up would briefly lose its indicator and stop control.
 */
function hasQueuedUserTurn(
    state: TranscriptState,
    stoppedUserTurnIds?: ReadonlySet<string>,
): boolean {
    for (const pending of state.pendingById.values()) {
        const operationId = state.operationIdByPendingId.get(pending.id);
        if (
            pending.type === 'user_message' &&
            pending.status === 'pending' &&
            !stoppedUserTurnIds?.has(pending.id) &&
            (operationId === undefined || !stoppedUserTurnIds?.has(operationId))
        ) {
            return true;
        }
    }
    return false;
}

export function deriveResponsePhase(
    evidence: ThinkingEvidence,
    state: TranscriptState,
    options: Readonly<{
        /** A stop this client requested that has not resolved or failed. */
        readonly localStopRequested: boolean;
        /**
         * The stop request itself is still on the wire. It wins over stale
         * terminal evidence so a click during a follow-up's send window
         * still presents as stopping and cannot be repeated.
         */
        readonly stopRequestInFlight: boolean;
        readonly stopRequest?: StopRequestProjection;
    }>,
): ResponsePhaseProjection {
    const stopRequest =
        options.stopRequest ?? projectStopRequests(state).current;
    const stoppedResponseEnded = stopRequest.stoppedResponseEnded;
    // A stopped stream can no longer confirm live work or echoes.
    const streamStopped = state.connection.kind === 'stopped';
    const segment = projectLatestSegment(state);
    const queuedUserTurn = hasQueuedUserTurn(
        state,
        stopRequest.stoppedUserTurnIds,
    );
    // A terminal event ends the response only while the run state is inert:
    // new work can restart under the same run without a fresh run_started
    // event, and that fresh activity outweighs the older terminal event. A
    // committed stop with nothing newer ends it unconditionally — run-state
    // claims during the wind-down are already spoken for. A queued user
    // turn reopens the response either way.
    const runEnded =
        !queuedUserTurn &&
        (evidence.kind === 'terminal' ||
            segment.stopIsLatest ||
            (segment.terminal &&
                (evidence.kind === 'idle' ||
                    evidence.kind === 'stale' ||
                    evidence.kind === 'interrupted')));
    // Once the server durably holds the stop — the pending event echoed, or
    // the run state acknowledges the interrupt — the response is over for
    // the user: the mic comes back while the run winds down out of sight.
    // "Stopping…" therefore only spans this client's own request until that
    // echo, and a stopped run's late activity claims stay quiet. A queued
    // user turn belongs to the next response, which that stop cannot hold.
    // Strong-activity handoff evidence can briefly outlive the lease. An
    // explicit interrupt with no remaining work still acknowledges Stop.
    const interruptedWithoutWork =
        state.runState?.interrupt_requested === true &&
        !state.runState.has_pending_work &&
        !state.runState.has_active_lease &&
        !state.runState.is_generating;
    const targetedStopRecorded = stopRequest.echoed && !stoppedResponseEnded;
    const stopTakenByServer =
        !queuedUserTurn &&
        (stopRequest.recorded ||
            targetedStopRecorded ||
            evidence.kind === 'interrupted' ||
            interruptedWithoutWork);
    if (
        !stopTakenByServer &&
        !stoppedResponseEnded &&
        (options.stopRequestInFlight ||
            (options.localStopRequested && !runEnded && !streamStopped))
    ) {
        return { phase: { kind: 'stopping' }, runEnded, stoppedResponseEnded };
    }
    if (!runEnded && !stopTakenByServer && !streamStopped) {
        const waitingFor = runStateWaitingFor(state.runState);
        if (evidence.kind === 'thinking') {
            return {
                phase: { kind: 'working', source: 'generating', waitingFor },
                runEnded,
                stoppedResponseEnded,
            };
        }
        if (evidence.kind === 'queued') {
            return {
                phase: { kind: 'working', source: 'queued', waitingFor },
                runEnded,
                stoppedResponseEnded,
            };
        }
        if (evidence.kind === 'background_work') {
            return {
                phase: { kind: 'working', source: 'background', waitingFor },
                runEnded,
                stoppedResponseEnded,
            };
        }
    }
    if (
        hasUnechoedSend(
            state,
            stopTakenByServer ? stopRequest.stoppedUserTurnIds : undefined,
            !streamStopped,
        )
    ) {
        return { phase: { kind: 'sending' }, runEnded, stoppedResponseEnded };
    }
    // Below live work and in-flight sends: a running answer keeps its
    // Thinking row, and a retry reads as sending again. Above waiting and
    // idle: an undelivered message is the thing that needs the user.
    if (hasFailedUnechoedSend(state)) {
        return {
            phase: { kind: 'needs_attention', reason: 'send_failed' },
            runEnded,
            stoppedResponseEnded,
        };
    }
    if (evidence.kind === 'waiting' && !runEnded) {
        return {
            phase: { kind: 'waiting', waitingFor: evidence.waitingFor },
            runEnded,
            stoppedResponseEnded,
        };
    }
    return { phase: { kind: 'idle' }, runEnded, stoppedResponseEnded };
}
